import { useEffect, useState } from "react";
import { Card } from "../components/Card";
import { Eyebrow } from "../components/Eyebrow";
import { formatDateTime } from "../lib/format";
import type { ProposalItem, ProposalSummary } from "../lib/types";

// GET /api/proposals?id= の返り値（PDF出力に使う分だけ）
type ProposalDetail = {
  proposal: {
    id: string;
    title: string | null;
    client_name: string | null;
    sales_rep: string | null;
    disclaimer: string | null;
  };
  items: {
    id: string;
    product_name: string | null;
    spec: unknown;
    schedule: unknown;
    selling_points: unknown;
    tooling_fee: number | string | null;
    sample_fee: number | string | null;
  }[];
  tiers: {
    proposal_item_id: string;
    qty: number | string;
    unit_price: number | string;
    calc_meta?: unknown;
  }[];
  images?: {
    proposal_item_id: string;
    url: string;
    is_primary: boolean;
    display_order: number | string;
  }[];
};

export function ProposalList() {
  const [rows, setRows] = useState<ProposalSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [menu, setMenu] = useState<string | null>(null); // "{rowId}:{kind}" の開いているポップオーバー

  useEffect(() => {
    fetch("/api/proposals")
      .then((r) => r.json())
      .then((d) => {
        if (d.ok) setRows(d.proposals ?? []);
        else setErr(d.error ?? "取得失敗");
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  async function exportPdf(
    row: ProposalSummary,
    kind: "proposal" | "quote",
    audience: "internal" | "client",
  ) {
    setBusyId(row.id);
    setMenu(null);
    setErr(null);
    try {
      const res = await fetch(`/api/proposals?id=${row.id}`);
      const d = (await res.json()) as { ok: boolean; error?: string } & ProposalDetail;
      if (!d.ok) throw new Error(d.error ?? "提案の取得に失敗しました");

      const items: ProposalItem[] = d.items.map((it) => ({
        product_name: it.product_name,
        spec: (it.spec ?? {}) as Record<string, unknown>,
        schedule: (it.schedule ?? {}) as Record<string, unknown>,
        selling_points: Array.isArray(it.selling_points) ? it.selling_points.map(String) : [],
        tooling_fee: it.tooling_fee != null ? Number(it.tooling_fee) : null,
        sample_fee: it.sample_fee != null ? Number(it.sample_fee) : null,
        tiers: d.tiers
          .filter((t) => t.proposal_item_id === it.id)
          .map((t) => {
            const meta = (t.calc_meta ?? {}) as { recommended?: number };
            const unit = Number(t.unit_price);
            const rec = meta.recommended != null ? Number(meta.recommended) : undefined;
            return {
              qty: Number(t.qty),
              unit_price: unit, // 確定値
              recommended_price: rec, // 推奨（社内PDF併記用・calc_metaから復元）
              is_overridden: rec != null && rec !== unit,
            };
          }),
        // 保存済み提案の採用画像を複数復元（proposal_item_images）
        images: (d.images ?? [])
          .filter((im) => im.proposal_item_id === it.id)
          .map((im) => ({ url: im.url, is_primary: im.is_primary })),
        imageUrl: (d.images ?? []).find((im) => im.proposal_item_id === it.id && im.is_primary)?.url ?? null,
      }));

      const pdf = await import("../lib/pdf");
      const header = {
        title: d.proposal.title,
        clientName: d.proposal.client_name,
        salesRep: d.proposal.sales_rep,
        disclaimer: d.proposal.disclaimer,
      };
      const bytes =
        kind === "proposal"
          ? await pdf.generateProposalPdf(header, items, { audience })
          : await pdf.generateQuotePdf(header, items, { audience });
      const dt = new Date();
      const ymd = `${dt.getFullYear()}${String(dt.getMonth() + 1).padStart(2, "0")}${String(dt.getDate()).padStart(2, "0")}`;
      const base = d.proposal.client_name || d.proposal.title || "提案";
      const suffix = audience === "internal" ? "_社内" : "";
      pdf.downloadPdf(bytes, `${kind === "proposal" ? "提案書" : "見積書"}_${base}_${ymd}${suffix}.pdf`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Eyebrow>History — 過去の提案</Eyebrow>
        <h2 className="text-xl font-semibold tracking-tight mt-1">過去の提案</h2>
      </div>
      {err && <p className="text-sm text-red-700">⚠ {err}</p>}
      <Card className="p-6">
        {loading ? (
          <p className="text-sm text-ink-muted">読み込み中…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-ink-muted">まだ保存された提案はありません。「提案を作る」から作成できます。</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-muted border-b border-ink/10">
                <th className="py-2 font-medium">タイトル</th>
                <th className="py-2 font-medium">提案先</th>
                <th className="py-2 font-medium">担当</th>
                <th className="py-2 font-medium text-right">商品数</th>
                <th className="py-2 font-medium text-right">更新</th>
                <th className="py-2 font-medium text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-ink/5">
                  <td className="py-2 font-medium">{p.title ?? "（無題）"}</td>
                  <td className="py-2">{p.client_name ?? "—"}</td>
                  <td className="py-2 text-ink-muted">{p.sales_rep ?? "—"}</td>
                  <td className="py-2 text-right">{p.item_count}</td>
                  <td className="py-2 text-right text-ink-muted">{formatDateTime(p.updated_at)}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <ListPdfButton
                      label={busyId === p.id ? "生成中…" : "提案書"}
                      disabled={busyId === p.id}
                      open={menu === `${p.id}:proposal`}
                      onToggle={() =>
                        setMenu((m) => (m === `${p.id}:proposal` ? null : `${p.id}:proposal`))
                      }
                      onPick={(a) => exportPdf(p, "proposal", a)}
                    />
                    <ListPdfButton
                      label="見積書"
                      disabled={busyId === p.id}
                      className="ml-1.5"
                      open={menu === `${p.id}:quote`}
                      onToggle={() =>
                        setMenu((m) => (m === `${p.id}:quote` ? null : `${p.id}:quote`))
                      }
                      onPick={(a) => exportPdf(p, "quote", a)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// 一覧行のPDF再出力ボタン。押すと「客先提出用 / 社内確認用」の2択を出す
function ListPdfButton({
  label,
  disabled,
  open,
  className = "",
  onToggle,
  onPick,
}: {
  label: string;
  disabled: boolean;
  open: boolean;
  className?: string;
  onToggle: () => void;
  onPick: (audience: "internal" | "client") => void;
}) {
  return (
    <span className={`relative inline-block ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={onToggle}
        className="text-xs px-2.5 py-1 rounded-full ring-1 ring-ink/15 bg-white hover:ring-ink/40 disabled:opacity-50"
      >
        {label}
      </button>
      {open && !disabled && (
        <div className="absolute right-0 z-10 mt-1 w-40 rounded-lg bg-white ring-1 ring-ink/15 shadow-lg p-1 text-left">
          <button
            type="button"
            onClick={() => onPick("client")}
            className="block w-full text-left px-3 py-1.5 rounded text-xs hover:bg-ink/[0.04]"
          >
            客先提出用
          </button>
          <button
            type="button"
            onClick={() => onPick("internal")}
            className="block w-full text-left px-3 py-1.5 rounded text-xs hover:bg-ink/[0.04]"
          >
            社内確認用
          </button>
        </div>
      )}
    </span>
  );
}
